import { TaskAdmin } from '@gleamer/types';

type ComputationStatus = TaskAdmin.ComputationStatus;
type ComputationTrigger = TaskAdmin.ComputationTrigger;
type PredictionImport = TaskAdmin.PredictionImport;
type PredictionImportAction = TaskAdmin.PredictionImportAction;
type PredictionImportIssue = TaskAdmin.PredictionImportIssue;
type PredictionImportModel = TaskAdmin.PredictionImportModel;
type PredictionImportStatus = TaskAdmin.PredictionImportStatus;

/**
 * What the task drawer says of a task's activity: the computations of its items and the imports of its
 * predictions — their labels, what each status means, and which actions an import's status allows.
 */

export const COMPUTATION_STATUS_LABELS: Record<ComputationStatus, string> = {
  RUNNING: 'Computing',
  DONE: 'Done',
  DONE_WITH_ISSUES: 'Done with issues',
  FAILED: 'Failed',
  SUPERSEDED: 'Superseded',
};

export const COMPUTATION_STATUS_MEANINGS: Record<ComputationStatus, string> = {
  RUNNING:
    'The items are being computed from the dataset table; labellers are held meanwhile.',
  DONE: 'Every item computed.',
  DONE_WITH_ISSUES:
    'The items are computed, but some rows, passes or items could not be: see its issues.',
  FAILED: 'The computation stopped: the task keeps the items it had.',
  SUPERSEDED: 'A later revision or reload started another computation.',
};

export const TRIGGER_LABELS: Record<ComputationTrigger, string> = {
  CREATE: 'Creation',
  REVISION: 'Revision',
  RELOAD: 'Reload',
};

export const IMPORT_STATUS_LABELS: Record<PredictionImportStatus, string> = {
  WAITING: 'Waiting',
  REQUESTED: 'Requested',
  IMPORTING: 'Importing',
  DONE: 'Done',
  PARTIAL_FAILURE: 'Partly failed',
  FAILED: 'Failed',
};

export const IMPORT_STATUS_MEANINGS: Record<PredictionImportStatus, string> = {
  WAITING:
    "Another import of the product is running: this one starts when it settles.",
  REQUESTED: 'Waiting for the importer to take it.',
  IMPORTING: 'The patients are being read from the table.',
  DONE: 'Every patient imported, or the rest accepted as it is.',
  PARTIAL_FAILURE:
    'Some patients failed: replay them, accept the import as it is, or abandon it.',
  FAILED:
    'The import stopped, or was abandoned: the task keeps its previous import.',
};

/** The settlements an import's status allows (viewer-api answers 409 to the others). */
export const IMPORT_ACTIONS: Record<
  PredictionImportAction,
  PredictionImportStatus[]
> = {
  replay: ['PARTIAL_FAILURE', 'FAILED'],
  accept: ['PARTIAL_FAILURE'],
  abandon: ['PARTIAL_FAILURE', 'WAITING', 'REQUESTED', 'IMPORTING'],
};

export function allowedImportActions(
  status: PredictionImportStatus
): PredictionImportAction[] {
  return (Object.keys(IMPORT_ACTIONS) as PredictionImportAction[]).filter(
    action => IMPORT_ACTIONS[action].includes(status)
  );
}

/** A table can be imported again unless one of the task's imports is still in flight. */
export function canImportAgain(imports: PredictionImport[]): boolean {
  return !imports.some(
    i => i.status === 'REQUESTED' || i.status === 'IMPORTING'
  );
}

/** The patient an issue is about: viewer-api's record names it `patientId`, the contract `patientPublicId`. */
export function issuePatient(issue: PredictionImportIssue): string {
  return issue.patientPublicId ?? issue.patientId ?? '—';
}

/** A model as `name version`, or its hash when it has no name. */
export function modelLabel(model: PredictionImportModel): string {
  const a = model.algorithm;
  const name = [a?.name, a?.version].filter(Boolean).join(' ');
  return name || a?.modelHash || 'Unknown model';
}

/** `1 h 05 min`, `3 min 20 s`, `12 s`: how long from `from` to `to` (or `now`). */
export function formatDuration(
  from: string,
  to?: string | null,
  now: Date = new Date()
): string {
  const start = new Date(from).getTime();
  const end = to ? new Date(to).getTime() : now.getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return '—';
  }
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h} h ${String(m).padStart(2, '0')} min`;
  }
  if (m > 0) {
    return `${m} min ${String(s).padStart(2, '0')} s`;
  }
  return `${s} s`;
}

/** The items settled of those expected, in percent; null while the table is still being read. */
export function progressPercent(
  settled: number,
  expected: number | null
): number | null {
  if (expected === null || expected === undefined) {
    return null;
  }
  if (expected <= 0) {
    return 100;
  }
  return Math.min(100, Math.round((100 * settled) / expected));
}
